import { api, apiFetch } from './client'

export type RolloutMode = 'dry_run' | 'live'

export const PRICE_ROLLOUT_PORTALS = ['base', 'amazon', 'ebay', 'kaufland', 'fnac', 'epages', 'nextore', 'shopify', 'metro'] as const
export type PriceRolloutPortal = typeof PRICE_ROLLOUT_PORTALS[number]

export interface RolloutRule {
  id: number
  portal: string
  account: string | null
  stock_item_id: number | null
  sku: string | null
  max_step_ct: number
  max_step_pct: number | null
  interval_days: number
  cooldown_days: number
  dead_band_ct: number
  dead_band_pct: number
  ceiling_pct_of_rrp: number | null
  min_price: number | null
  dr_propagates: boolean
  direct_push_adapter: string | null
  is_enabled: boolean
  mode: RolloutMode
  updated_at: string
}

export interface SetRulePayload {
  account?: string | null
  max_step_ct: number
  max_step_pct?: number | null
  interval_days: number
  cooldown_days: number
  dead_band_ct: number
  dead_band_pct: number
  ceiling_pct_of_rrp?: number | null
  min_price?: number | null
  dr_propagates: boolean
  direct_push_adapter?: 'amazon_spapi' | 'shopify' | 'ebay_trading' | null
  is_enabled: boolean
  mode: RolloutMode
}

/** Amazon-LIVE laeuft ausschliesslich ueber den SP-API-Direct-Push-Writer. */
export const AMAZON_DIRECT_PUSH_ADAPTER = 'amazon_spapi' as const

/**
 * Zusaetzliche serverseitige Gates fuer einen echten Amazon-Preisschritt — die UI schaltet
 * davon nichts frei, sie kann den Schritt nur vorbereiten.
 */
export const AMAZON_LIVE_GATES_HINT =
  'Zusätzlich serverseitig nötig: globale Live-Freigabe, Amazon-Gebühren-Gate und die exakte '
  + 'Konto+SellerSKU-Allowlist. Die Oberfläche allein aktiviert keinen Preisschritt.'

/** Amazon + LIVE = Direct-Push. dry_run und alle anderen Portale bleiben unberuehrt. */
export function isAmazonLiveRule(portal: string, mode: RolloutMode): boolean {
  return portal === 'amazon' && mode === 'live'
}

/**
 * Normalisiert Amazon-LIVE-Payloads hart: der Preis geht ueber die SP-API, DreamRobot ist dort
 * nur noch Doku. `dr_propagates=false` schliesst eine versteckte zweite Preis-Autoritaet aus.
 * Fail-safe an der API-Grenze angewandt, damit kein Aufrufer daran vorbeikommt.
 */
export function hardenAmazonLivePayload(portal: string, payload: SetRulePayload): SetRulePayload {
  if (!isAmazonLiveRule(portal, payload.mode)) return payload
  return { ...payload, direct_push_adapter: AMAZON_DIRECT_PUSH_ADAPTER, dr_propagates: false }
}

export async function listRules(): Promise<RolloutRule[]> {
  const res = await api.get<{ rules: RolloutRule[] }>('/pricing/rollout/rules/')
  return res.rules
}

export async function setDefaultRule(portal: string, payload: SetRulePayload): Promise<void> {
  await apiFetch(`/pricing/rollout/rules/${encodeURIComponent(portal)}/`, {
    method: 'PUT', body: JSON.stringify(hardenAmazonLivePayload(portal, payload)),
  })
}

export async function setOverrideRule(portal: string, sku: string, payload: SetRulePayload): Promise<void> {
  await apiFetch(`/pricing/rollout/rules/${encodeURIComponent(portal)}/sku/${encodeURIComponent(sku)}/`, {
    method: 'PUT', body: JSON.stringify(hardenAmazonLivePayload(portal, payload)),
  })
}

export async function deleteOverrideRule(portal: string, sku: string): Promise<void> {
  await apiFetch(`/pricing/rollout/rules/${encodeURIComponent(portal)}/sku/${encodeURIComponent(sku)}/`, {
    method: 'DELETE',
  })
}

// ── S3: einheitliche Kennung, Zeitplan, Betrieb (Spiegel von src/modules/price-rollout/kennung.ts, zeitplan.ts, ansicht.ts) ──

export type SetInfo =
  | { art: 'einzel' }
  | { art: 'menge'; menge: number; komponente_id: number }
  | { art: 'set'; teile: number; komponenten: { id: number; menge: number }[] }

export interface KennungRef { stock_item_id: number; sku: string; stern: string | null; name: string | null }

/** Stern (`merchant_sku`) groß, DR-Nummer (`sku`) klein, Einzelstück/×n/Set, Grundartikel (FK-Elternteil). */
export interface Kennung extends KennungRef {
  ist_grundartikel: boolean
  grundartikel: KennungRef | null
  set: SetInfo
}

export type ZeitplanArt = 'plan' | 'nur_protokoll' | 'blockiert' | 'fertig'

/** Prognose der kommenden Tagesläufe (Backend `rolloutZeitplan`) — „frühestens“, keine Garantie. */
export interface Zeitplan {
  art: ZeitplanArt
  grund: string | null
  gekoppelt: boolean
  faellig_ab: string | null
  naechster_versuch: string | null
  naechster_preis: number | null
  schritte: number | null
  ziel_am: string | null
  endpreis: number | null
  ziel_grund: 'grenze' | 'zu_lang' | 'amazon_budget' | null
  warte_grund: string | null
}

export interface Betrieb {
  kill_aktiv: boolean
  live_writes: boolean
  tagespruefung: boolean
  heute_gelaufen: boolean
  start_schritt: boolean
  amazon_fee_gate_offen: boolean
}

/** Effektive Regel zur Anzeige; im Paar trägt die Basis Schritt/Takt/Sperre der eBay-Regel. */
export interface RegelAnzeige {
  max_step_ct: number
  max_step_pct: number | null
  interval_days: number
  cooldown_days: number
  mode: RolloutMode
  is_enabled: boolean
  takt_von_ebay: boolean
}

/** `erbt_basis`: eBay hat keinen eigenen DR-Preis und übernimmt den Basispreis (Tick-Grund `ebay_folgt_basis`, eBay nie selbst geschrieben). */
export type IstQuelle = 'ausstehend' | 'zuletzt_gesetzt' | 'angebot' | 'dr_preis' | 'erbt_basis'

export interface RolloutPlanAnzeige {
  zeitplan: Zeitplan
  regel: RegelAnzeige | null
  gekoppelt: boolean
  schreibweg: string | null
  ist_preis: number | null
  ist_quelle: IstQuelle | null
  versand: ShippingPolicy
}

export interface RolloutStatus {
  id: number
  stock_item_id: number
  sku: string | null
  name: string | null
  portal: string
  account: string | null
  target_price: number
  konto_aufschlag_ct?: number
  konto_aufschlag_soll_ct?: number
  konto_aufschlag_konto?: string | null
  last_applied_price: number | null
  last_step_at: string | null
  locked_until: string | null
  status: 'active' | 'paused' | 'done' | 'error'
  error_message: string | null
  updated_at: string
  // S3 (additiv): nur Anzeige.
  kennung?: Kennung | null
  versand?: ShippingPolicy | null
  wirkung_konfiguriert?: ScopeWirkung
  wirkung_jetzt?: { art: ZeitplanArt; grund: string | null }
  schreibweg?: string | null
  regel?: RegelAnzeige | null
  gekoppelt?: boolean
  ist_preis?: number | null
  ist_quelle?: IstQuelle | null
  zeitplan?: Zeitplan
}

export interface RolloutStatusAntwort { rollouts: RolloutStatus[]; betrieb: Betrieb | null }

/** Rollouts samt Betriebszustand (Not-Aus, LIVE-Freigabe, Tagesprüfung) — Query-Key `['rollout-status']`. */
export async function getRolloutStatus(sku?: string): Promise<RolloutStatusAntwort> {
  const q = sku ? `?sku=${encodeURIComponent(sku)}` : ''
  const res = await api.get<{ rollouts: RolloutStatus[]; betrieb?: Betrieb }>(`/pricing/rollout/status/${q}`)
  return { rollouts: res.rollouts, betrieb: res.betrieb ?? null }
}

export async function listRollouts(sku?: string): Promise<RolloutStatus[]> {
  return (await getRolloutStatus(sku)).rollouts
}

export async function setRolloutStatus(id: number, status: 'paused' | 'active'): Promise<void> {
  await apiFetch(`/pricing/rollout/rollouts/${id}/`, { method: 'PATCH', body: JSON.stringify({ status }) })
}

export async function pauseAll(): Promise<number> {
  const res = await api.post<{ paused: number }>('/pricing/rollout/pause/')
  return res.paused
}
/** Setzt pausierte Rollouts fort; Plus-Versand-Rollouts bleiben pausiert und werden gezählt. */
export async function resumeAll(): Promise<{ resumed: number; skipped_exkl: number }> {
  const res = await api.post<{ resumed: number; skipped_exkl?: number }>('/pricing/rollout/resume/')
  return { resumed: res.resumed, skipped_exkl: res.skipped_exkl ?? 0 }
}
export async function killAll(): Promise<number> {
  const res = await api.post<{ paused: number }>('/pricing/rollout/kill/')
  return res.paused
}
/** Hebt den Not-Aus auf. Pausierte Rollouts bleiben pausiert. */
export async function clearKill(): Promise<void> {
  await api.post<{ kill_active: boolean }>('/pricing/rollout/clear-kill/')
}

export interface PriceHistoryEntry {
  id: number
  stock_item_id: number
  sku: string | null
  portal: string
  account: string | null
  writer: string | null
  old_price: number | null
  new_price: number | null
  target_price: number | null
  step_ct_applied: number | null
  reason: string
  applied_at: string
  success: boolean
  error: string | null
  delta?: number | null
  ebay_konto?: string | null
  konto_aufschlag_ct?: number | null
  status?: 'fehlgeschlagen' | 'protokoll' | 'ueberholt' | 'portal_bestaetigt' | 'abweichend' | 'dr_gesetzt' | 'unbekannt'
  ist_jetzt?: number | null
  ist_stand?: string | null
  kennung?: Kennung | null
  /** Portal-Angebot zum Nachprüfen (eBay-ItemID, Amazon-ASIN …); fehlt bei älterem Backend. */
  angebote?: { portal: string; konto?: string; nummer: string; url: string }[]
}

/** Suche: `DR-…` = DR-Nummer, `*367`/`367` = Sternnummer (ganze Familie). */
export interface HistoryParams { sku?: string; portal?: string; konto?: string; von?: string; bis?: string; art?: 'aenderungen' | 'alle' | 'fehler'; richtung?: 'hoch' | 'runter'; vor?: string; limit?: number }
export interface HistoryResult { history: PriceHistoryEntry[]; familien: number | null; naechster: string | null; zusammenfassung: { anzahl: number; hoch: number; runter: number } }
export interface KontoAufschlagZeile { account: string; aufschlag_ct: number; varianten_aktiv: number; rollouts_offen: number; rollouts_veraltet: number; rollouts_gekappt: number; updated_at: string | null; updated_by: string | null }
export interface KontoAufschlagUebersicht { konten: KontoAufschlagZeile[]; mehrfach_konten: { anzahl: number; beispiele: string[] } }
export async function getKontoAufschlag(): Promise<KontoAufschlagUebersicht> { return api.get<KontoAufschlagUebersicht>('/pricing/rollout/konto-aufschlag/') }
export async function setKontoAufschlag(account: string, aufschlag_ct: number): Promise<KontoAufschlagZeile> { return api.put<KontoAufschlagZeile>(`/pricing/rollout/konto-aufschlag/ebay/${encodeURIComponent(account)}/`, { aufschlag_ct }) }
export function listHistory(params: HistoryParams): Promise<HistoryResult>
export function listHistory(sku?: string, limit?: number): Promise<PriceHistoryEntry[]>
export async function listHistory(input?: string | HistoryParams, limit = 100): Promise<HistoryResult | PriceHistoryEntry[]> {
  const params = new URLSearchParams()
  const query: HistoryParams = typeof input === 'object' ? input : { ...(input ? { sku: input } : {}), limit, art: 'alle' }
  for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value))
  const res = await api.get<HistoryResult>(`/pricing/rollout/history/?${params.toString()}`)
  return typeof input === 'object' ? res : res.history
}

/**
 * Start-Schritt (Plan 2026-10-19 S2.1): Ergebnis des sofortigen ersten Schritts beim Anlegen.
 * `aus` = Schalter PRICE_ROLLOUT_START_STEP_ENABLED nicht gesetzt; `nichts_neu` = nur Ziel geaendert.
 */
export type FirstStep =
  | { status: 'aus' | 'nichts_neu' | 'kill' | 'lease_busy' | 'already_done' }
  | { status: 'ran'; results: { rolloutId: number; portal: string; action: string }[] }
  | { status: 'fehler'; error: string }

export interface CreateRolloutAck { id: number; created: boolean; debounced?: boolean; first_step?: FirstStep }

export async function createRollout(sku: string, portal: string, targetPrice: number): Promise<CreateRolloutAck> {
  return api.post<CreateRolloutAck>('/pricing/rollout/rollouts/', {
    sku, portal, target_price: targetPrice,
  })
}

// ── Preis-Rollout im Grundartikel (S2) ────────────────────────────────────────
// Spiegel von `GET /pricing/rollout/family/:sku/` (Backend src/modules/price-rollout/family.ts).
// Alles hier ist Anzeige: Ziel, Floor, Ceiling, Urteil und Folge je Scope rechnet nur das Backend.

export type FamilyPortal = 'amazon' | 'ebay' | 'shopify' | 'metro' | 'kaufland'
export type ShippingPolicy = 'inkl' | 'exkl'
export type AngebotsZustandWert = 'aktiv' | 'unbestaetigt' | 'offline' | 'ausgeschlossen' | 'kein_angebot'
export type ScopeFolge = 'neu' | 'ziel_aendern' | 'unveraendert_dead_band' | 'reaktiviert'
export type ScopeWirkung = 'keine_regel' | 'regel_aus' | 'dry_run' | 'gesperrt' | 'live'

export interface AngebotsZustand {
  zustand: AngebotsZustandWert
  ist_preis: number | null
  versand_frei: boolean | null
  versand: number | null
  konten: number
  offline_seit: string | null
  zuletzt_gesehen: string | null
  angebote: {
    konto: string; status: string; preis: number | null; zuletzt_gesehen: string | null; url: string
    /** Nur Amazon: Parent-ASIN der Variationsfamilie (fehlt bei älterem Backend; null = unbekannt/keine Variante). */
    parent_asin?: string | null
  }[]
  ernte: boolean
}

export interface RegelInfo {
  aktiv: boolean; modus: RolloutMode; adapter: string | null; override: boolean
  /** S3: eigene Regel — im Paar gilt für die Basis der eBay-Takt (`plan.regel`). */
  schritt_ct?: number; schritt_pct?: number | null; takt_tage?: number; sperre_tage?: number
}
export interface FamilyRolloutInfo {
  id: number; status: string; ziel: number; letzter_preis: number | null; fehler: string | null
  plan?: RolloutPlanAnzeige | null
}

export interface PortalZeile {
  angebot: AngebotsZustand
  cia_aktiv: boolean
  politik: ShippingPolicy
  regel: RegelInfo | null
  /** Was der Tick mit einem Rollout dieses Portals täte. */
  wirkung: ScopeWirkung
  floor: number
  ceiling: number | null
  /** A2: Kundenversand K des Portals (inkl 0, unbekannt null); Floor exkl = ceilCt(Break-Even inkl) − K. */
  kundenversand: number | null
  set_ek_unbekannt: boolean
  /** Woraus Floor/Break-Even entstehen (Backend, gleiche Rechnung wie der Floor). */
  kalkulation: Kalkulation
  /** Verlust je Verkauf beim Ist-Preis; null = kein Verlust oder kein Ist-Preis. */
  verlust_je_verkauf: number | null
  rollout: FamilyRolloutInfo | null
}

export interface Kalkulation {
  ek: number
  /** Versand inkl. Verpackung = porto + verpackung. */
  versand: number
  /** Kaskade oder GLS-Tarif der Versandregel (Flag im Backend). */
  versand_stufe: 'brief' | 'warenpost' | 'dhl' | 'gls' | 'gls_uebergroesse_150' | 'gls_uebergroesse_gt150'
  /** Brief über der Preisschwelle → als Warenpost gerechnet. */
  hochgestuft: boolean
  porto: number
  verpackung: number
  gebuehren_pct: number
  /** Break-Even der Politik (inkl: mit Versand; exkl A2: Break-Even inkl − Kundenversand, K unbekannt ⇒ inkl). */
  break_even: number
  /** Mindestpreis der Regel; Floor = max(Break-Even, Mindestpreis). */
  min_preis: number | null
}

export interface ScopeVorschau {
  aufschlag: { soll_ct: number; wirksam_ct: number; konto: string | null; hinweis: 'mehrere_konten' | 'kein_ebay_konto' | 'gekappt' | null } | null
  portal: string
  account: string | null
  ziel: number | null
  regel: RegelInfo | null
  floor: number
  ceiling: number | null
  bestehend: { id: number; status: string; ziel: number; fehler: string | null; plan?: RolloutPlanAnzeige | null } | null
  folge: ScopeFolge
  wirkung: ScopeWirkung
  /** S3: wohin ein Schritt schreibt; null ohne Regel. */
  schreibweg?: string | null
  /** S3: Prognose für den Zustand nach dem Start. */
  nachher?: RolloutPlanAnzeige | null
  /** A2b F2: Ziel auf den Amazon-Gesamtpreis angehoben. */
  angehoben?: true
}

export interface FamilyStart {
  portal: 'amazon' | 'base'
  ziel: number | null
  startbar: boolean
  grund: string | null
  server_grund: string | null
  fanout_portal: string | null
  scopes: ScopeVorschau[]
  alle_dry_run: boolean
}

export interface FamilySpur {
  ziel_endziel: number | null
  politik: ShippingPolicy
  /** A2: null = kein Ziel (Grund in `sperre`). */
  ziel: number | null
  /** A2: Kundenversand des Kanals (inkl 0, unbekannt null). */
  kundenversand?: number | null
  sperre?: string | null
  ist_preis: number | null
  strategie: 'margin' | 'break_even'
  warnung: boolean
  cia_aktiv: boolean
  /** Ausnahme „Ist-Preis halten“ (nur gültige; fehlt bei älterem Backend). */
  ausnahme?: SpurAusnahme | null
}

/** Vom Nutzer gehaltener Ist-Preis; gilt, solange Strategie und Ziel dem Stand bei Anlage entsprechen. */
export interface SpurAusnahme {
  preis: number
  ziel_bei_anlage: number
  seit: string
  von: string | null
}

export interface FamilyVariante {
  sku: string
  stock_item_id: number
  name: string
  merchant_sku: string | null
  is_grundartikel: boolean
  set_menge: number | null
  kennung?: Kennung | null
  strategie: { amazon: 'margin' | 'break_even'; base: 'margin' | 'break_even'; uneinheitlich: boolean }
  spuren: { amazon: FamilySpur; base: FamilySpur }
  portale: Record<FamilyPortal, PortalZeile>
  start: { amazon: FamilyStart; base: FamilyStart }
}

export interface RolloutFamily {
  sku: string
  stock_item_id: number
  kill_aktiv: boolean
  live_writes: boolean
  amazon_fee_gate_offen: boolean
  kennung?: Kennung | null
  betrieb?: Betrieb
  ernte: Record<FamilyPortal, string | null>
  varianten: FamilyVariante[]
}

export async function getRolloutFamily(sku: string): Promise<RolloutFamily> {
  return api.get<RolloutFamily>(`/pricing/rollout/family/${encodeURIComponent(sku)}/`)
}

/** Hält den aktuellen Ist-Preis der Spur (Ziel/Ist liest der Server); laufende Rollouts der Spur pausieren. */
export async function setAusnahme(sku: string, spur: 'amazon' | 'base'): Promise<{ preis: number; ziel_bei_anlage: number; pausiert: number }> {
  return api.put(`/pricing/rollout/ausnahme/${encodeURIComponent(sku)}/`, { spur })
}

/** Hebt die Ausnahme auf; pausierte Rollouts bleiben pausiert, ein neuer Start ist wieder möglich. */
export async function hebeAusnahmeAuf(sku: string, spur: 'amazon' | 'base'): Promise<void> {
  await api.delete(`/pricing/rollout/ausnahme/${encodeURIComponent(sku)}/?spur=${spur}`)
}

// ── S4: Verlauf + Verkauf seit letzter Änderung (GET /pricing/rollout/verlauf/) ──

export type VerlaufAusloeser = 'start' | 'tageslauf' | 'manuell' | 'unbekannt'
export type VerlaufArt = 'start' | 'schritt' | 'ziel' | 'nachgetragen'
export type VerkaufsKanal = 'amazon' | 'ebay' | 'shopify' | 'metro' | 'kaufland' | 'sonst'

export interface VerlaufAenderung {
  id: number; portal: string; account: string | null
  alt: number | null; neu: number; at: string
  art: VerlaufArt; ausloeser: VerlaufAusloeser
}

export interface VerkaufsZahl { stueck: number; auftraege: number }

/** Verkaufte Angebote (Stück) und Aufträge seit der letzten Änderung; `davor` = gleich lange Spanne direkt davor. */
export interface VerlaufVerkauf extends VerkaufsZahl {
  seit: string
  /** `aenderung` = seit der letzten Preisänderung; `start` = noch keine Änderung, seit dem ersten Lauf. */
  ab: 'aenderung' | 'start'
  tage: number
  davor: VerkaufsZahl
  /** Aufteilung über alle Kanäle; die Kopfzahl zählt nur `kanal_filter`. */
  kanaele: ({ kanal: VerkaufsKanal } & VerkaufsZahl)[]
  kanal_filter: VerkaufsKanal[]
  /** Nur Basis: Kanäle mit eigenem Rollout desselben Artikels — nicht in der Kopfzahl. */
  kanal_eigen: VerkaufsKanal[]
}

export interface RolloutVerlauf {
  rollout_id: number
  /** Im Paar Basis + eBay beide Portale — beide ids liefern dieselbe Zeile. */
  portale: string[]
  /** Neueste zuerst, höchstens 20. */
  aenderungen: VerlaufAenderung[]
  anzahl: number
  verkauf: VerlaufVerkauf | null
}

/** Verlauf je Rollout-id (höchstens 50); unbekannte ids fehlen. Query-Key `['rollout-verlauf', ids]`. */
export async function getRolloutVerlauf(ids: readonly number[]): Promise<Record<string, RolloutVerlauf>> {
  const res = await api.get<{ verlauf: Record<string, RolloutVerlauf> }>(`/pricing/rollout/verlauf/?ids=${ids.join(',')}`)
  return res.verlauf
}

export interface RolloutUebersicht { aktiv: number; ausgerollt: number; am_ziel: number }

export function getRolloutUebersicht(skus: string[], signal?: AbortSignal): Promise<Record<string, RolloutUebersicht | null>> {
  return apiFetch(`/pricing/rollout/uebersicht/?skus=${encodeURIComponent(skus.join(','))}`, { signal })
}
